import { DimensionValue, StyleSheet, View } from 'react-native';
import 'reflect-metadata'; // importing CredentialExchangeRecord throws an uncaught error without this
import './i18n';
import AppRouter from './routes/AppRouter';
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { FullScreenSpinner } from './components/molecules/Spinner';
import ToastProvider from './contexts/ToastContext';
import ContextProvider from './contexts/CredentialContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import log from './logger';
import FallbackPage from './pages/FallbackPage';

const styles = StyleSheet.create({
  appViewStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh' as DimensionValue,
    width: '100vw' as DimensionValue
  }
});

/**
 * @description App component
 * @return {JSX.Element}
 */
function App(): JSX.Element {
  const authContext = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  const [walletId, setWalletId] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkFirstLogin = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/api/user/getaccount', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authContext.user?.access_token}`
          }
        });

        if (!response.ok) {
          setIsLoading(false);
          return;
        }

        const data = await response.json();

        setShowWelcome(!data.firstLogin);
        setWalletId(data.walletId);
        setIsLoading(false);
      } catch (error) {
        log.error(error);
        setIsLoading(false);
      }
    };

    if (authContext?.isLoading === false) {
      setWalletId('');
      checkFirstLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authContext?.isAuthenticated]);

  if (isLoading) {
    return (
      <View style={styles.appViewStyle}>
        <FullScreenSpinner />
      </View>
    );
  }

  // user has logged in but no wallet id is set
  if (!walletId && !isLoading && authContext?.isAuthenticated) {
    return (
      <ThemeProvider>
        <ToastProvider>
          <View style={styles.appViewStyle}>
            <FallbackPage />
          </View>
        </ToastProvider>
      </ThemeProvider>
    );
  }

  return (
    <WebSocketProvider walletId={walletId}>
      <ThemeProvider>
        <ContextProvider>
          <ToastProvider>
            <View style={styles.appViewStyle}>
              <AppRouter showWelcome={showWelcome} />
            </View>
          </ToastProvider>
        </ContextProvider>
      </ThemeProvider>
    </WebSocketProvider>
  );
}

export default App;
